/**
 * Reviewer-Aktionsleiste im Meldungs-Detail (F17/F18):
 * sichten, bearbeiten, ablehnen, zusammenführen, freigeben, in Umsetzung.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"
import type { ReportDetail, ReviewRequest } from "@/types"

type Dialog = "ablehnen" | "zusammenfuehren" | "bearbeiten" | null

export function ReviewAktionen({ report }: { report: ReportDetail }) {
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<Dialog>(null)
  const [begruendung, setBegruendung] = useState("")
  const [zielId, setZielId] = useState("")
  const [titel, setTitel] = useState(report.titel)
  const [ist, setIst] = useState(report.istBeschreibung ?? "")
  const [soll, setSoll] = useState(report.sollBeschreibung ?? "")
  const [kriterien, setKriterien] = useState((report.akzeptanzkriterien ?? []).join("\n"))

  const fertig = (meldung: string) => {
    void queryClient.invalidateQueries({ queryKey: ["reports"] })
    setDialog(null)
    setBegruendung("")
    toast.success(meldung)
  }

  const review = useMutation({
    mutationFn: (body: ReviewRequest) => api.reports.review(report.id, body),
    onSuccess: (_r, body) =>
      fertig(
        {
          gesichtet: "Als gesichtet markiert",
          ablehnen: "Meldung abgelehnt",
          zusammenfuehren: "Meldungen zusammengeführt",
          in_umsetzung: "In Umsetzung gesetzt",
        }[body.aktion]
      ),
    onError: (e) => toast.error(apiError(e)),
  })

  const freigeben = useMutation({
    mutationFn: () => api.reports.freigeben(report.id),
    onSuccess: (r) => fertig(`Freigegeben — Forgejo-Issue #${r.forgejoIssueNr}`),
    onError: (e) => toast.error(apiError(e)),
  })

  const bearbeiten = useMutation({
    mutationFn: () =>
      api.reports.update(report.id, {
        titel,
        istBeschreibung: ist || null,
        sollBeschreibung: soll || null,
        akzeptanzkriterien: kriterien
          .split("\n")
          .map((z) => z.trim())
          .filter(Boolean),
      }),
    onSuccess: () => fertig("Anforderung aktualisiert"),
    onError: (e) => toast.error(apiError(e)),
  })

  const knopf = "rounded-lg px-4 py-2 text-[13px] font-semibold disabled:opacity-50"

  return (
    <section className="rounded-xl border bg-card p-5 shadow-card">
      <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground/70">
        Review (netz-fabrik)
      </h2>
      <div className="flex flex-wrap items-center gap-2">
        {report.status === "neu" && (
          <button
            type="button"
            onClick={() => review.mutate({ aktion: "gesichtet" })}
            className={`${knopf} bg-primary text-primary-foreground`}
          >
            Als gesichtet markieren
          </button>
        )}
        {report.status === "gesichtet" && (
          <button
            type="button"
            onClick={() => freigeben.mutate()}
            disabled={freigeben.isPending}
            title="Erzeugt das Forgejo-Issue (F18)"
            className={`${knopf} bg-primary text-primary-foreground`}
          >
            {freigeben.isPending ? "Erzeuge Issue …" : "Freigeben → Forgejo-Issue"}
          </button>
        )}
        {report.status === "freigegeben" && (
          <button
            type="button"
            onClick={() => review.mutate({ aktion: "in_umsetzung" })}
            className={`${knopf} bg-primary text-primary-foreground`}
          >
            In Umsetzung
          </button>
        )}
        {(report.status === "neu" || report.status === "gesichtet") && (
          <>
            <button
              type="button"
              onClick={() => setDialog("bearbeiten")}
              className={`${knopf} bg-secondary`}
            >
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={() => setDialog("zusammenfuehren")}
              className={`${knopf} bg-secondary`}
            >
              Zusammenführen
            </button>
            <button
              type="button"
              onClick={() => setDialog("ablehnen")}
              className={`${knopf} border border-destructive/30 bg-card text-destructive hover:bg-destructive/5`}
            >
              Ablehnen
            </button>
          </>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => void api.reports.exportDownload(report.id, "markdown")}
          className={`${knopf} bg-secondary`}
          title="Mini-PRD als Markdown (F15)"
        >
          Export .md
        </button>
        <button
          type="button"
          onClick={() => void api.reports.exportDownload(report.id, "json")}
          className={`${knopf} bg-secondary`}
          title="Übergabe-Metadaten als JSON (F15)"
        >
          Export .json
        </button>
      </div>

      {dialog !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4">
          <div className="w-full max-w-lg rounded-[14px] bg-card p-5 shadow-2xl">
            {dialog === "ablehnen" && (
              <>
                <h3 className="mb-2 font-semibold">Meldung ablehnen</h3>
                <p className="mb-3 text-sm text-muted-foreground">
                  Die Begründung ist für den Melder sichtbar (F17).
                </p>
                <textarea
                  value={begruendung}
                  onChange={(e) => setBegruendung(e.target.value)}
                  rows={3}
                  placeholder="Begründung (Pflicht)"
                  className="mb-3 w-full rounded-md border px-3 py-1.5 text-sm"
                />
              </>
            )}
            {dialog === "zusammenfuehren" && (
              <>
                <h3 className="mb-2 font-semibold">Mit bestehender Meldung zusammenführen</h3>
                <p className="mb-3 text-sm text-muted-foreground">
                  Diese Meldung wird als Duplikat der Ziel-Meldung abgelehnt.
                </p>
                <input
                  value={zielId}
                  onChange={(e) => setZielId(e.target.value)}
                  placeholder="Nummer der Ziel-Meldung, z. B. 2"
                  className="mb-2 w-full rounded-md border px-3 py-1.5 text-sm"
                />
                <textarea
                  value={begruendung}
                  onChange={(e) => setBegruendung(e.target.value)}
                  rows={2}
                  placeholder="Zusatz-Begründung (optional)"
                  className="mb-3 w-full rounded-md border px-3 py-1.5 text-sm"
                />
              </>
            )}
            {dialog === "bearbeiten" && (
              <>
                <h3 className="mb-2 font-semibold">Anforderung bearbeiten (F17)</h3>
                <label className="mb-2 block text-sm">
                  <span className="mb-1 block font-medium">Titel</span>
                  <input
                    value={titel}
                    onChange={(e) => setTitel(e.target.value)}
                    maxLength={255}
                    className="w-full rounded-md border px-3 py-1.5"
                  />
                </label>
                <label className="mb-2 block text-sm">
                  <span className="mb-1 block font-medium">Ist</span>
                  <textarea
                    value={ist}
                    onChange={(e) => setIst(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border px-3 py-1.5"
                  />
                </label>
                <label className="mb-2 block text-sm">
                  <span className="mb-1 block font-medium">Soll</span>
                  <textarea
                    value={soll}
                    onChange={(e) => setSoll(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border px-3 py-1.5"
                  />
                </label>
                <label className="mb-3 block text-sm">
                  <span className="mb-1 block font-medium">
                    Akzeptanzkriterien (eins pro Zeile)
                  </span>
                  <textarea
                    value={kriterien}
                    onChange={(e) => setKriterien(e.target.value)}
                    rows={4}
                    className="w-full rounded-md border px-3 py-1.5"
                  />
                </label>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDialog(null)}
                className={`${knopf} bg-secondary`}
              >
                Abbrechen
              </button>
              {dialog === "ablehnen" && (
                <button
                  type="button"
                  onClick={() =>
                    review.mutate({ aktion: "ablehnen", begruendung: begruendung.trim() })
                  }
                  disabled={!begruendung.trim() || review.isPending}
                  className={`${knopf} bg-destructive text-destructive-foreground`}
                >
                  Ablehnen
                </button>
              )}
              {dialog === "zusammenfuehren" && (
                <button
                  type="button"
                  onClick={() =>
                    review.mutate({
                      aktion: "zusammenfuehren",
                      zielId: Number(zielId),
                      begruendung: begruendung.trim() || undefined,
                    })
                  }
                  disabled={!/^\d+$/.test(zielId) || review.isPending}
                  className={`${knopf} bg-primary text-primary-foreground`}
                >
                  Zusammenführen
                </button>
              )}
              {dialog === "bearbeiten" && (
                <button
                  type="button"
                  onClick={() => bearbeiten.mutate()}
                  disabled={!titel.trim() || bearbeiten.isPending}
                  className={`${knopf} bg-primary text-primary-foreground`}
                >
                  Speichern
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
